import React, { Fragment, PureComponent } from 'react';
import {
  Field,
  getIn,
  connect as connectFormik,
  FieldProps,
  FormikProps,
} from 'formik';
import { TagChoiceWrapper, TagChoiceEditorFields, RowWrapper } from './styles';
import Icon from 'components/Icon';
import MultiChoiceEditor from '../../MultiChoice';
import Input from 'components/Input';
import notify from 'components/Notify';
import Select, { IOption } from 'components/Select/Select';
import Button from '../../../../../../Button/Button';
import { getTags } from '../../GeneralQuestion/GeneralQuestionEditor';
import { removeDuplicates } from '../../../../../../../utils/happyFunctions';
import Collapsible from '../../../../../../Collapsible/Collapsible';
import { ITags, ITagView } from 'store/providers/providerTypes';
import {
  ICompany,
  ICompanyPermissionScheme,
} from 'store/companies/companiesTypes';
import {
  IChoiceView,
  IFormValues,
  IMultipleChoiceQuestionView,
  IQuestionTags,
  ISurveyView,
} from 'store/survey/surveyTypes';
import i18n from '../../../../../../../i18n';
import ButtonGroup from '../../../../../../ButtonGroup/ButtonGroup';
import { black } from 'utils/style/colors';

interface IOwnProps {
  questionId: number;
  showSecondary?: boolean;
  tags: IQuestionTags;
  allTags?: ITags;
  isDistinctive?: boolean;
  company?: ICompany;
  survey?: ISurveyView;
}

interface IFormikConnect {
  formik: FormikProps<IFormValues>;
}

type IComponentProps = IOwnProps & IFormikConnect;

export const convertTagIDs = (tagIDs: number[], allTags: IQuestionTags) => {
  return tagIDs
    .map((tagID) => {
      return { label: allTags[tagID]?.text, value: tagID };
    })
    .filter((tag: IOption) => !!tag);
};

const convertTags = (tags: IOption[]) => {
  return tags.reduce((tagIDs: number[], tag) => {
    tagIDs.push(+tag.value);
    return tagIDs;
  }, []);
};

const viewModes: {
  [key in IMultipleChoiceQuestionView['viewMode']]: IOption;
} = {
  list: {
    value: 'list',
    label: i18n.t('listView', { ns: 'survey' }) as string,
  },
  popup: {
    value: 'popup',
    label: i18n.t('popupView', { ns: 'survey' }) as string,
  },
};

const buttonGroupOptions = {
  chooseChoice: i18n.t('chooseChoice', { ns: 'survey' }) as string,
  chooseTag: i18n.t('chooseTag', { ns: 'survey' }) as string,
};

class TagChoiceEditor extends PureComponent<IComponentProps> {
  handleChangeMultiSelect =
    (onChange: (e: React.ChangeEvent<any>) => void, distinctive: boolean) =>
    (e: React.ChangeEvent<any>) => {
      if (distinctive) {
        notify(
          i18n.t('aQuestoinCouldNotBeDistinctiveAndMultiSelectAtSameTime', {
            ns: 'survey',
          }) as string,
          'warning'
        );
      } else {
        onChange(e);
      }
    };

  moveParentTagsToTags = (form: FormikProps<any>) => {
    const {
      formik: { values: question },
      tags,
    } = this.props;
    const parentTagChoice = getIn(question, ['data', 'parentTagChoices']) || [];
    const excludedTagChoice =
      getIn(question, ['data', 'excludedTagChoices']) || [];
    const tagChoices = getIn(question, ['data', 'tagChoices']) || [];
    const toAddTags = Object.values(tags).reduce(
      (current: number[], item: ITagView) => {
        if (
          parentTagChoice.includes(item.parentId) &&
          !tagChoices.includes(item.id) &&
          !excludedTagChoice?.includes(item.id) &&
          !item.deleted
        )
          current.push(Number(item.id));
        return current;
      },
      [] as number[]
    );
    tagChoices.push(...toAddTags);
    form.setFieldValue('data.parentTagChoices', []);
    form.setFieldValue('data.excludedTagChoices', []);
    form.setFieldValue('data.tagChoices', tagChoices);
  };

  handleSetLevelTitles = (index: number, value: string, list: string[]) => {
    return list.map((item, ind) => {
      if (ind === index) return value;
      return item;
    });
  };

  render() {
    const {
      formik: { values: question },
      tags,
      questionId,
      company,
    } = this.props;
    const parentTagChoice = getIn(question, ['data', 'parentTagChoices']);
    const excludedTagChoice = getIn(question, ['data', 'excludedTagChoices']);
    const tagChoices = getIn(question, ['data', 'tagChoices']);
    const useTags = getIn(question, ['data', 'useTags']);
    const multiSelect = getIn(question, ['data', 'multiSelect']);
    const goDeepInTags = getIn(question, ['data', 'goDeepInTags']);
    const levelTitles = getIn(question, ['data', 'levelTitles']);
    const distinctive = getIn(question, ['data', 'distinctive']);
    const unifyValueAndText = getIn(question, ['data', 'unifyValueAndText']);
    let currentChoiceOptions: IOption[] = [];
    if (useTags && tags) {
      if (parentTagChoice) {
        parentTagChoice.forEach((parentId: number) => {
          Object.values(tags)
            .filter(
              (tag) =>
                tag.parentId === parentId &&
                !tag.deleted &&
                !excludedTagChoice?.includes(tag.id)
            )
            .map((item: any) =>
              currentChoiceOptions.push({
                value: item.id,
                label: item.text,
              })
            );
        });
      }
      if (tagChoices) {
        tagChoices.map((item: any) =>
          currentChoiceOptions.push({
            value: tags[item]?.id,
            label: tags[item]?.text,
          })
        );
      }
      // number[]
      currentChoiceOptions = removeDuplicates(currentChoiceOptions);
    } else {
      const question = this.props.formik.values;
      const choices = getIn(question, ['data', 'choiceList']);
      currentChoiceOptions =
        choices
          ?.filter((e: IChoiceView) => e.value)
          ?.map((e: IChoiceView) => ({ value: e.value, label: e.text })) || [];
    }

    const permissionSchemes = company?.permissionSchemes
      ? Object.values(company?.permissionSchemes).reduce(
          (current: IOption[], item: ICompanyPermissionScheme) => {
            current.push({
              label: item.properties.name,
              value: item.id!,
            });
            return current;
          },
          [] as IOption[]
        )
      : [];
    const surveyType = this.props.survey?.surveyType;
    const isExam = surveyType === 'Exam' || surveyType === 'Bank';

    return (
      <TagChoiceWrapper>
        {!isExam && (
          <>
            <TagChoiceEditorFields>
              <Field name="data.viewMode">
                {({ form, field }: FieldProps) => (
                  <Select
                    options={Object.values(viewModes)}
                    values={[viewModes[field.value]]}
                    onChangeValues={(views) => {
                      const typeValue = views[0]
                        ? views[0].value
                        : viewModes.list.value; // select list view by default
                      form.setFieldValue('data.viewMode', typeValue);
                    }}
                    label={
                      i18n.t('choicesViewMode', {
                        ns: 'survey',
                      }) as string
                    }
                  />
                )}
              </Field>

              <Field
                name="data.multiSelect"
                render={({ field }: FieldProps) => (
                  <React.Fragment>
                    <Input
                      {...field}
                      id={`multiSelect-${questionId}`}
                      type="checkbox"
                      label={
                        i18n.t(
                          'theAnswerHasTheAbilityToSelectMultipleOptions',
                          {
                            ns: 'survey',
                          }
                        ) as string
                      }
                      checked={multiSelect}
                      onChange={this.handleChangeMultiSelect(
                        field.onChange,
                        distinctive
                      )}
                    />
                  </React.Fragment>
                )}
              />
              <Field
                name="data.defaultValue"
                render={({ form, field }: FieldProps) => (
                  <React.Fragment>
                    <Select
                      onChangeValues={(values) => {
                        const typeValue = values.map((e) => e.value); // select list view by default
                        form.setFieldValue('data.defaultValue', typeValue);
                      }}
                      options={currentChoiceOptions}
                      values={
                        field.value?.reduce((current: IOption[], key: any) => {
                          const foundChoice = currentChoiceOptions.find(
                            (it) => it.value === key
                          );
                          if (foundChoice) current.push(foundChoice);
                          return current;
                        }, []) ?? []
                      }
                      label={
                        i18n.t('defaultValue', {
                          ns: 'survey',
                        }) as string
                      }
                      multiChoice={multiSelect}
                    />
                  </React.Fragment>
                )}
              />
            </TagChoiceEditorFields>
            <span style={{ color: black }}>
              {i18n.t('chooseChoiceType', { ns: 'survey' }) as string}
            </span>
            <Field name="data.useTags">
              {({ form }: FieldProps) => (
                <ButtonGroup
                  items={buttonGroupOptions}
                  selected={useTags ? 'chooseTag' : 'chooseChoice'}
                  onSelect={(itemKey) => {
                    if (itemKey === 'chooseTag')
                      form.setFieldValue('data.useTags', true);
                    else form.setFieldValue('data.useTags', false);
                    form.setFieldValue('data.weight', 1);
                  }}
                />
              )}
            </Field>
          </>
        )}
        {useTags ? (
          <Fragment>
            <p style={{ color: black }}>
              <Icon type="check-circle" className="right" />
              {i18n.t('choicesMessage', { ns: 'survey' }) as string}
            </p>
            <span>
              <Field name="data.parentTagChoices">
                {({ form }: FieldProps) => (
                  <div>
                    <Select
                      label={i18n.t('allSubTagsOf', { ns: 'survey' }) as string}
                      values={
                        parentTagChoice
                          ? convertTagIDs(parentTagChoice, tags)
                          : []
                      }
                      options={tags ? getTags(tags, true) : []}
                      multiChoice={true}
                      onChangeValues={(selectedTags) => {
                        form.setFieldValue(
                          'data.parentTagChoices',
                          convertTags(selectedTags)
                        );
                      }}
                      placeholder={
                        i18n.t('selectTag', { ns: 'survey' }) as string
                      }
                    />
                    <Button
                      style={{ float: 'left' }}
                      onClick={() => this.moveParentTagsToTags(form)}
                    >
                      <Icon type="arrow-down" />
                      {
                        i18n.t('moveToTags', {
                          ns: 'survey',
                        }) as string
                      }
                    </Button>
                  </div>
                )}
              </Field>
            </span>
            <span>
              <Field name="data.tagChoices">
                {({ form }: FieldProps) => (
                  <Select
                    label={i18n.t('allTagsOf', { ns: 'survey' }) as string}
                    values={tagChoices ? convertTagIDs(tagChoices, tags) : []}
                    options={tags ? getTags(tags, true) : []}
                    multiChoice={true}
                    placeholder={
                      i18n.t('selectTag', { ns: 'survey' }) as string
                    }
                    onChangeValues={(selectedTags) => {
                      form.setFieldValue(
                        'data.tagChoices',
                        convertTags(selectedTags)
                      );
                    }}
                  />
                )}
              </Field>
            </span>
            {(parentTagChoice?.size || excludedTagChoice?.size || true) && (
              <span>
                <Field name="data.excludedTagChoices">
                  {({ form }: FieldProps) => (
                    <div>
                      <Select
                        label={i18n.t('exclude', { ns: 'survey' }) as string}
                        values={
                          excludedTagChoice
                            ? convertTagIDs(excludedTagChoice, tags)
                            : []
                        }
                        options={
                          tags ? getTags(tags, true, parentTagChoice) : []
                        }
                        multiChoice={true}
                        onChangeValues={(selectedTags) => {
                          form.setFieldValue(
                            'data.excludedTagChoices',
                            convertTags(selectedTags)
                          );
                        }}
                        placeholder={
                          i18n.t('selectTag', {
                            ns: 'survey',
                          }) as string
                        }
                      />
                    </div>
                  )}
                </Field>
              </span>
            )}

            {/*<Field*/}
            {/*  name="data.goDeepInTags"*/}
            {/*  render={({ field }: FieldProps) => (*/}
            {/*    <React.Fragment>*/}
            {/*      <Input*/}
            {/*        {...field}*/}
            {/*        id={`goDeepInTags-${questionId}-checkbox`}*/}
            {/*        type="checkbox"*/}
            {/*        label={*/}
            {/*          i18n.t('goDeepInTags', {*/}
            {/*            ns: 'survey',*/}
            {/*          }) as string*/}
            {/*        }*/}
            {/*        checked={goDeepInTags}*/}
            {/*      />*/}
            {/*    </React.Fragment>*/}
            {/*  )}*/}
            {/*/>*/}

            <Field
              name="data.goDeepInTags"
              render={({ field, form }: FieldProps) => (
                <React.Fragment>
                  <Input
                    {...field}
                    id={`goDeepInTags-${questionId}`}
                    type="text"
                    label={
                      i18n.t('goDeepInTags', {
                        ns: 'survey',
                      }) as string
                    }
                    placeholder={goDeepInTags || 0}
                    value={goDeepInTags || 0}
                    onChange={(event) => {
                      form.setFieldValue(
                        'data.goDeepInTags',
                        event.target.value
                      );
                      if (goDeepInTags + 1 < levelTitles.length) {
                        form.setFieldValue(
                          'data.levelTitles',
                          levelTitles.slice(0, goDeepInTags + 1)
                        );
                      }
                    }}
                  />
                </React.Fragment>
              )}
            />
            {Number(goDeepInTags) > 0 && (
              <div className="flex gap-3 flex-wrap">
                {Array.from(
                  { length: (Number(goDeepInTags) || 0) + 1 },
                  (_, index) => index
                ).map((item) => {
                  if (item === 0)
                    return (
                      <Field
                        key={`levelTitles-${item}`}
                        name="data.levelTitles"
                        render={({ form }: FieldProps) => (
                          <React.Fragment>
                            <Input
                              id={`levelTitles-${item}`}
                              key={item}
                              type="text"
                              placeholder={i18n.t('questionTagTitle', {
                                ns: 'survey',
                              })}
                              value={levelTitles?.[0] || ''}
                              title={i18n.t('questionTagTitle', {
                                ns: 'survey',
                              })}
                              onChange={(event) => {
                                let customLevelTitles;
                                if (levelTitles?.length !== goDeepInTags + 1) {
                                  customLevelTitles = Array.from(
                                    {
                                      length: (Number(goDeepInTags) || 0) + 1,
                                    },
                                    (_, index) => index
                                  ).map(
                                    (currentIndex) =>
                                      levelTitles?.[currentIndex] || ''
                                  );
                                } else {
                                  customLevelTitles = levelTitles;
                                }

                                form.setFieldValue(
                                  'data.levelTitles',
                                  this.handleSetLevelTitles(
                                    0,
                                    event.target.value,
                                    customLevelTitles
                                  )
                                );
                              }}
                            />
                          </React.Fragment>
                        )}
                      />
                    );
                  else
                    return (
                      <Field
                        key={`levelTitles-${item}`}
                        name="data.levelTitles"
                        render={({ form }: FieldProps) => (
                          <React.Fragment>
                            <Input
                              id={`levelTitles-${item}`}
                              type="text"
                              placeholder={i18n.t('depth', {
                                ns: 'survey',
                                number: item,
                              })}
                              value={levelTitles?.[item] || ''}
                              title={i18n.t('depth', {
                                ns: 'survey',
                                number: item,
                              })}
                              onChange={(event) => {
                                let customLevelTitles;
                                if (levelTitles?.length !== goDeepInTags + 1) {
                                  customLevelTitles = Array.from(
                                    {
                                      length: (Number(goDeepInTags) || 0) + 1,
                                    },
                                    (_, index) => index
                                  ).map(
                                    (currentIndex) =>
                                      levelTitles?.[currentIndex] || ''
                                  );
                                } else {
                                  customLevelTitles = levelTitles;
                                }
                                form.setFieldValue(
                                  'data.levelTitles',
                                  this.handleSetLevelTitles(
                                    item,
                                    event.target.value,
                                    customLevelTitles
                                  )
                                );
                              }}
                            />
                          </React.Fragment>
                        )}
                      />
                    );
                })}
              </div>
            )}

            {(permissionSchemes?.length ?? 0) > 0 && (
              <Collapsible
                header={
                  i18n.t('permissionScheme', {
                    ns: 'survey',
                  }) as string
                }
                iconName="lock"
                style={{ overflow: 'visible' }}
                customBodyStyle={{ overflow: 'visible' }}
              >
                <RowWrapper className="">
                  <Field
                    name="data.fillPermissionSchemeList"
                    render={({ form, field }: FieldProps) => {
                      const findOption = permissionSchemes.reduce(
                        (current: IOption[], item: IOption) => {
                          if (field.value?.includes(item.value)) {
                            current.push(item);
                          }
                          return current;
                        },
                        [] as IOption[]
                      );
                      return (
                        <React.Fragment>
                          <Select
                            containerStyle={{ width: '400px' }}
                            onChangeValues={(values) => {
                              const schemesIds = values.reduce(
                                (current: number[], value: IOption) => {
                                  current.push(value.value as number);
                                  return current;
                                },
                                []
                              );
                              form.setFieldValue(
                                'data.fillPermissionSchemeList',
                                schemesIds
                              );
                            }}
                            options={permissionSchemes}
                            values={findOption}
                            label={
                              i18n.t('fillPermissionSchemeList', {
                                ns: 'survey',
                              }) as string
                            }
                            multiChoice={true}
                          />
                        </React.Fragment>
                      );
                    }}
                  />

                  <Field
                    name="data.reportPermissionSchemeList"
                    render={({ form, field }: FieldProps) => {
                      const findOption = permissionSchemes.reduce(
                        (current: IOption[], item: IOption) => {
                          if (field.value?.includes(item.value)) {
                            current.push(item);
                          }
                          return current;
                        },
                        [] as IOption[]
                      );
                      return (
                        <React.Fragment>
                          <Select
                            containerStyle={{ width: '400px' }}
                            onChangeValues={(values) => {
                              const schemesIds = values.reduce(
                                (current: number[], value: IOption) => {
                                  current.push(value.value as number);
                                  return current;
                                },
                                []
                              );
                              form.setFieldValue(
                                'data.reportPermissionSchemeList',
                                schemesIds
                              );
                            }}
                            options={permissionSchemes}
                            values={findOption}
                            label={
                              i18n.t('reportPermissionSchemeList', {
                                ns: 'survey',
                              }) as string
                            }
                            multiChoice={true}
                          />
                        </React.Fragment>
                      );
                    }}
                  />
                  {this.props.showSecondary && (
                    <>
                      <Field
                        name="data.secondaryFillPermissionSchemeList"
                        render={({ form, field }: FieldProps) => {
                          const findOption = permissionSchemes.reduce(
                            (current: IOption[], item: IOption) => {
                              if (field.value?.includes(item.value)) {
                                current.push(item);
                              }
                              return current;
                            },
                            [] as IOption[]
                          );
                          return (
                            <React.Fragment>
                              <Select
                                containerStyle={{ width: '400px' }}
                                onChangeValues={(values) => {
                                  const schemesIds = values.reduce(
                                    (current: number[], value: IOption) => {
                                      current.push(value.value as number);
                                      return current;
                                    },
                                    []
                                  );
                                  form.setFieldValue(
                                    'data.secondaryFillPermissionSchemeList',
                                    schemesIds
                                  );
                                }}
                                options={permissionSchemes}
                                values={findOption}
                                label={
                                  i18n.t('analyseFillPermissionSchemeList', {
                                    ns: 'survey',
                                  }) as string
                                }
                                multiChoice={true}
                              />
                            </React.Fragment>
                          );
                        }}
                      />

                      <Field
                        name="data.secondaryReportPermissionSchemeList"
                        render={({ form, field }: FieldProps) => {
                          const findOption = permissionSchemes.reduce(
                            (current: IOption[], item: IOption) => {
                              if (field.value?.includes(item.value)) {
                                current.push(item);
                              }
                              return current;
                            },
                            [] as IOption[]
                          );
                          return (
                            <React.Fragment>
                              <Select
                                containerStyle={{ width: '400px' }}
                                onChangeValues={(values) => {
                                  const schemesIds = values.reduce(
                                    (current: number[], value: IOption) => {
                                      current.push(value.value as number);
                                      return current;
                                    },
                                    []
                                  );
                                  form.setFieldValue(
                                    'data.secondaryReportPermissionSchemeList',
                                    schemesIds
                                  );
                                }}
                                options={permissionSchemes}
                                values={findOption}
                                label={
                                  i18n.t('analyseReportPermissionSchemeList', {
                                    ns: 'survey',
                                  }) as string
                                }
                                multiChoice={true}
                              />
                            </React.Fragment>
                          );
                        }}
                      />
                    </>
                  )}
                </RowWrapper>
              </Collapsible>
            )}
          </Fragment>
        ) : (
          <span>
            <MultiChoiceEditor
              messageName="choiceList"
              hasScore={true}
              preventEdit={unifyValueAndText}
              allowMigrateToTag={true}
              tags={tags}
              isExam={isExam}
            >
              <span style={{ color: black }}>
                <Icon type="check-circle" className="right" />
                {i18n.t('choicesMessage', { ns: 'survey' }) as string}
              </span>
            </MultiChoiceEditor>
          </span>
        )}
      </TagChoiceWrapper>
    );
  }
}

export default connectFormik<IOwnProps, IFormValues>(TagChoiceEditor);
